/** @jsx jsx */

import { useEffect, useState } from 'react';

import {
  Alert,
  CopyTextInput,
  Field,
  Grid,
  LegacyButton,
  LegacyIconButton,
  Spinner,
  Text,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import Link from 'app/common/components/Link';
import LoadingBlock from 'app/common/components/LoadingBlock';
import {
  useMutationRegenerateIntegrationToken,
  useQueryIntegration,
  useQueryIntegrationToken,
  useRouter,
} from 'app/developer-v3/hooks';
import useZapierElements from 'app/developer-v3/hooks/useZapierElements';
import { URLS } from 'app/common/constants';
import { LayoutStyles } from 'app/developer-v3/layouts/constants';
import { getAppUrl } from 'app/developer-v3/utils';

// Client IDs for Zapier Issue Manager embed
const ZIM_CLIENT_ID_STAGING = 'pYMZ1OGwAbHntoa1pWgfst6aRx9jipv0XvtE07NA'; // App ID 213012
const ZIM_CLIENT_ID_PROD = 'cOyG6pUbDTtX6i8F4eQJutA3Sc39Y8B1vnzGdziM'; // App ID 171110
const isStaging = location.hostname.includes('zapier-staging.com');
const ZIM_CLIENT_ID = isStaging ? ZIM_CLIENT_ID_STAGING : ZIM_CLIENT_ID_PROD;

const Styles = {
  root: css`
    padding: ${LayoutStyles.verticalPadding} ${LayoutStyles.horizontalPadding};
  `,
  bulletList: css`
    list-style: disc inside;
    margin-bottom: 15px;
  `,
};

function ZapierIssueManager() {
  const { params, routeTo } = useRouter();
  const integrationId = Number(params.id);
  const [isTokenVisible, setIsTokenVisible] = useState(false);

  useZapierElements();

  const {
    data: integration,
    isLoading: isLoadingIntegration,
  } = useQueryIntegration({ integrationId });
  const isPublished = integration?.isBeta || integration?.public;

  const {
    data: tokenData,
    isLoading: isLoadingToken,
  } = useQueryIntegrationToken({ integrationId }, { staleTime: 300000 });

  const {
    mutate: regenerateToken,
    isLoading: isRegenerating,
  } = useMutationRegenerateIntegrationToken({ integrationId });

  useEffect(() => {
    if (!isLoadingIntegration && !isPublished) {
      routeTo(`${getAppUrl(integrationId)}/issues`);
    }
  }, [isLoadingIntegration, isPublished, integrationId, routeTo]);

  const isLoading = isLoadingIntegration || isLoadingToken;

  return isLoading ? (
    <LoadingBlock />
  ) : (
    <div css={Styles.root}>
      <Text fontWeight={600} tag="h1" type="SectionHeader">
        Zapier Issue Manager
      </Text>
      <Text margin="0 0 15px" tag="p">
        Sync, monitor, and respond to your integration's issues in your
        preferred issue-tracking tool using{' '}
        <Link
          href={`${URLS.zapbook}/zapier-issue-manager/integrations`}
          target="_blank"
        >
          Zapier Issue Manager
        </Link>
        .
      </Text>
      {tokenData?.token ? (
        <ul css={Styles.bulletList}>
          <li>
            <Text>
              Get notified when Zapier's support team files new bugs or feature
              requests
            </Text>
          </li>
          <li>
            <Text>
              Track issue status changes and new comments in real time
            </Text>
          </li>
          <li>
            <Text>
              Update an issue's work status and respond to issues without
              leaving your tools
            </Text>
          </li>
          <li>
            <Text>
              Generate reports comparing issue trends across time periods
            </Text>
          </li>
        </ul>
      ) : (
        <Alert status="warning" title="Token not available">
          The API tokens are being rolled out to existing integrations and will
          be available soon.
        </Alert>
      )}
      {tokenData?.token && (
        <Grid gap="15px">
          <Field
            label="API Token"
            renderHelpText={() =>
              'Use this token to authenticate Zapier Issue Manage. Regenerating the token will invalidate any existing connections.'
            }
            renderInput={inputProps => (
              <Grid alignItems="center" columns="1fr min-content" gap="15px">
                <CopyTextInput
                  {...inputProps}
                  appendContent={
                    isRegenerating ? (
                      <Spinner />
                    ) : (
                      <LegacyIconButton
                        icon={isTokenVisible ? 'miscEyeClosed' : 'miscEye'}
                        onClick={() => setIsTokenVisible(!isTokenVisible)}
                      />
                    )
                  }
                  aria-label="API Token"
                  canClickIconAfter={isTokenVisible}
                  name="apiToken"
                  type={isTokenVisible ? 'text' : 'password'}
                  value={tokenData.token}
                />
                <LegacyButton
                  disabled={isRegenerating}
                  onClick={() => regenerateToken()}
                >
                  Regenerate
                </LegacyButton>
              </Grid>
            )}
          />
          <zapier-workflow
            client-id={ZIM_CLIENT_ID}
            guess-zap-display="show"
            intro-copy-display="hide"
            theme="light"
            zap-create-from-scratch-display="hide"
          />
        </Grid>
      )}
    </div>
  );
}

export default ZapierIssueManager;
